import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { api } from "../lib/api";
import { Mark } from "./Mark";

/**
 * The client half of the gate.
 *
 * The server is what actually enforces this — every `/api` route 401s without
 * a session, and that is the security boundary. This exists so a signed-out
 * visitor sees a login page instead of an app full of failed requests. Never
 * mistake it for the enforcement: it runs in the browser, where the user
 * decides what executes.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    // A session can be revoked mid-visit; the API will refuse anyway, but this
    // is what moves the page rather than leaving errors on screen.
    refetchInterval: 60_000,
    retry: false,
  });

  if (session.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Mark size={44} animate loop />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  if (!session.data?.authenticated) {
    // A recognised account that is not yet active gets the explanation rather
    // than a login form it has already filled in. Everyone else gets the
    // landing page: a first-time visitor arriving at `/` should be told what
    // this is before being asked to sign in to it.
    return (
      <Navigate to={session.data?.status ? "/pending" : "/login"} replace />
    );
  }

  return <>{children}</>;
}
